import moment from 'moment';
import React from 'react';
import ReactModal from 'react-modal';
import styled from 'styled-components';
import { Button } from '../atoms/Buttons';
import Headings from '../atoms/Headings';
import { localize } from '../atoms/LocalizedMask';
import { MoneyAccountHookForm } from '../organisms/campaign/EditMoneyAccountHook';

type Props = {
  isOpen: boolean;
  accountHook: MoneyAccountHookForm | null;
  comparingAccountHook?: MoneyAccountHookForm | null;
  onClose: () => void;
  onConfirm: () => void;
};

const modalStyles = {
  overlay: {
    zIndex: 9999,
  },
  content: {
    padding: 40,
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-30%',
    transform: 'translate(-50%, -50%)',
  },
};

const ModalHeader = styled(Headings.h4)`
  font-size: 20px;
  text-align: center;
  margin: 0;
  width: 100%;
  font-weight: 700;
`;

const ModalContent = styled.div`
  margin: 60px 0;
  display: flex;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 24px;
`;

const ColumnSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Divider = styled.div`
  border-right: 1px solid #e1e1e1;
  margin: 0 60px;
`;

const ColumnHeader = styled(Headings.h5)`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  margin-bottom: 8px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionHeader = styled(Headings.h6)`
  display: flex;
  gap: 24px;
  color: #757575;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  margin: 0;
`;

const SectionLabel = styled.label`
  width: 200px;
  color: #757575;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  margin: 0;
  display: flex;
  column-gap: 12px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const InfoText = styled.p<{ isBold?: boolean }>`
  margin: 0;
  color: #222;
  font-size: 16px;
  font-style: normal;
  font-weight: ${({ isBold = false }) => (isBold ? '700' : '400')};
  line-height: 24px;
  flex: 1;
`;

const SubSection = styled.section`
  display: flex;
  gap: 24px;
  > ${SectionLabel} {
    padding-left: 20px;
    font-weight: 400;
  }
`;

const EditedBadgeContainer = styled.span`
  border-radius: 4px;
  background: #21bf25;
  width: 64px;
  color: #fff;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  display: inline-block;
`;

const EditedBadge = () => <EditedBadgeContainer>変更あり</EditedBadgeContainer>;

const SAME_ACCOUNT_HOOK_COMPARISON = {
  name: true,
  description: true,
  dateRange: true,
  pointAmount: true,
  pointExpiresAt: true,
  pointExpiresInDays: true,
  usageLimit: true,
};

type AccountHookComparisonModal = typeof SAME_ACCOUNT_HOOK_COMPARISON;

const MoneyAccountHookModal = ({
  isOpen,
  accountHook,
  comparingAccountHook,
  onClose,
  onConfirm,
}: Props) => {
  if (!accountHook) return null;

  const accountHookComparison: AccountHookComparisonModal = !comparingAccountHook
    ? SAME_ACCOUNT_HOOK_COMPARISON
    : {
        name: accountHook.name === comparingAccountHook.name,
        description: accountHook.description === comparingAccountHook.description,
        dateRange:
          (accountHook.dateRange?.from?.isSame(comparingAccountHook.dateRange?.from) ?? true) &&
          (accountHook.dateRange?.to?.isSame(comparingAccountHook.dateRange?.to) ?? true),
        pointAmount: accountHook.pointAmount === comparingAccountHook.pointAmount,
        pointExpiresAt:
          (accountHook.pointExpiresAt === null && comparingAccountHook.pointExpiresAt === null) ||
          moment(accountHook.pointExpiresAt).isSame(comparingAccountHook.pointExpiresAt),
        pointExpiresInDays:
          (accountHook.pointExpiresInDays || '') ===
          (comparingAccountHook.pointExpiresInDays || ''),
        usageLimit: (accountHook.usageLimit || '') === (comparingAccountHook.usageLimit || ''),
      };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick
      closeTimeoutMS={200}
      ariaHideApp={false}
      style={modalStyles}
    >
      <ModalHeader>
        {comparingAccountHook
          ? '以下の内容でキャンペーンを更新します'
          : '以下の内容でキャンペーンを登録します'}
      </ModalHeader>
      <ModalContent>
        <ColumnSection>
          <ColumnHeader>基本設定</ColumnHeader>

          {!comparingAccountHook && (
            <Section>
              <SectionHeader>
                <SectionLabel>マネー</SectionLabel>
                <InfoText>{accountHook.privateMoney?.name}</InfoText>
              </SectionHeader>
            </Section>
          )}

          {!comparingAccountHook && (
            <Section>
              <SectionHeader>送金元店舗</SectionHeader>

              <SubSection>
                <SectionLabel>組織</SectionLabel>
                <InfoText>{accountHook.senderOrganization?.name}</InfoText>
              </SubSection>

              <SubSection>
                <SectionLabel>店舗</SectionLabel>
                <InfoText>{accountHook.senderShop?.name}</InfoText>
              </SubSection>
            </Section>
          )}

          <Section>
            <SectionHeader>キャンペーン</SectionHeader>

            <SubSection>
              <SectionLabel>
                キャンペーン名 {!accountHookComparison.name && <EditedBadge />}
              </SectionLabel>
              <InfoText isBold={!accountHookComparison.name}>{accountHook.name}</InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                キャンペーン説明 {!accountHookComparison.description && <EditedBadge />}
              </SectionLabel>
              <InfoText isBold={!accountHookComparison.description}>
                {accountHook.description || '設定なし'}
              </InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                対象期間 {!accountHookComparison.dateRange && <EditedBadge />}
              </SectionLabel>
              <InfoText isBold={!accountHookComparison.dateRange}>
                {accountHook.dateRange?.from?.format('YYYY/MM/DD HH:mm:ss') ?? '設定なし'} ～
                {accountHook.dateRange?.to?.format('YYYY/MM/DD HH:mm:ss') ?? '設定なし'}
              </InfoText>
            </SubSection>
          </Section>
        </ColumnSection>

        <Divider />

        <ColumnSection>
          <ColumnHeader>詳細設定</ColumnHeader>

          <Section>
            <SectionHeader>
              <SectionLabel>
                ポイント金額 {!accountHookComparison.pointAmount && <EditedBadge />}
              </SectionLabel>
              <InfoText isBold={!accountHookComparison.pointAmount}>
                {localize(accountHook.pointAmount.toString() ?? '')}
              </InfoText>
            </SectionHeader>
          </Section>

          <Section>
            <SectionHeader>ポイント有効期限</SectionHeader>

            <SubSection>
              <SectionLabel>
                日時指定 {!accountHookComparison.pointExpiresAt && <EditedBadge />}
              </SectionLabel>
              <InfoText isBold={!accountHookComparison.pointExpiresAt}>
                {accountHook.pointExpiresAt?.format('YYYY/MM/DD HH:mm:ss') || '設定なし'}
              </InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                相対日数指定 {!accountHookComparison.pointExpiresInDays && <EditedBadge />}
              </SectionLabel>
              <InfoText isBold={!accountHookComparison.pointExpiresInDays}>
                {accountHook.pointExpiresInDays || '設定なし'}
              </InfoText>
            </SubSection>
          </Section>

          {!comparingAccountHook && (
            <Section>
              <SectionHeader>ポイント負担先店舗</SectionHeader>

              <SubSection>
                <SectionLabel>組織</SectionLabel>
                <InfoText>{accountHook.bearPointOrganization?.name || '設定なし'}</InfoText>
              </SubSection>

              <SubSection>
                <SectionLabel>店舗</SectionLabel>
                <InfoText>{accountHook.bearPointShop?.name || '設定なし'}</InfoText>
              </SubSection>
            </Section>
          )}

          <Section>
            <SectionHeader>
              <SectionLabel>
                付与上限人数 {!accountHookComparison.usageLimit && <EditedBadge />}
              </SectionLabel>
              <InfoText isBold={!accountHookComparison.usageLimit}>
                {localize(accountHook.usageLimit?.toString() ?? '') || '設定なし'}
              </InfoText>
            </SectionHeader>
          </Section>
        </ColumnSection>
      </ModalContent>

      <ModalFooter>
        <Button variant="neutral" onClick={onClose}>
          キャンセル
        </Button>
        <Button variant="contained" onClick={onConfirm}>
          {comparingAccountHook ? '更新' : '登録'}
        </Button>
      </ModalFooter>
    </ReactModal>
  );
};

export default MoneyAccountHookModal;
